import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  HostBinding,
} from '@angular/core';
import { Subscription, finalize, Subject, takeUntil } from 'rxjs';

import {
  AddressCandidateSearchBestOptions,
  AddressItemResponse,
  AddressService,
  SuggestItemResponse,
} from '../../../../services/address.service';
import { LoadingController } from '../../../../helpers/loading.controller';
import { ScreenService } from 'app/shared/services/screen.service';
import { TranslationService } from '@services/translation.service';

@Component({
  selector: 'app-address-search-bar',
  templateUrl: 'address-search-bar.component.html',
  styleUrls: ['address-search-bar.component.scss'],
})
export class AddressSearchBarComponent implements OnDestroy {
  private lifecycle = new Subject<void>();

  @Output()
  onItemChange = new EventEmitter<AddressItemResponse>(true);

  @Input()
  searchDebounce = 300;
  @Input()
  maxSuggestions = 6;

  opened: boolean;

  @HostBinding('class.mobile-mode')
  isMobileScreen: boolean = false;

  searchLoading = new LoadingController();

  private addressItemSelected: AddressItemResponse;

  private searchTerm: string;
  private lastSearchTerm: string;

  private addressCandidateSubscription: Subscription;

  constructor(
    private addressService: AddressService,
    private screenService: ScreenService,
    public ts: TranslationService
  ) {}

  dataSource = (text: string) => {
    text = this.handleSearchTerm(text);
    this.searchTerm = text;
    this.addressCandidateSubscription?.unsubscribe();
    if (!text) {
      return [];
    }

    return this.addressService.findSuggests(text, this.maxSuggestions);
  };

  ngOnInit() {
    this.screenService
      .observeIsMobile()
      .pipe(takeUntil(this.lifecycle))
      .subscribe((isMobile) => {
        this.isMobileScreen = isMobile;
      });
  }

  ngOnDestroy() {
    this.addressCandidateSubscription?.unsubscribe();

    this.dataSource = null;
  }

  onKeydown({ event }) {
    if (event?.key === 'Enter') {
      this.searchBestAddressCandidateByTerm();
    }
  }

  onBtnSearchClick() {
    this.searchBestAddressCandidateByTerm();
  }

  onSearchValueChanged(text: string) {
    if (text) {
      return;
    }

    this.lastSearchTerm = this.searchTerm = null;

    if (this.addressItemSelected) {
      this.addressItemSelected = null;
      this.onItemChange.emit(this.addressItemSelected);
    }
  }

  onItemSelectedInternal(item: SuggestItemResponse) {
    this.addressCandidateSubscription?.unsubscribe();

    this.lastSearchTerm = this.searchTerm;
    if (item) {
      this.searchBestAddressCandidate({
        text: item.text,
        key: item.key,
      });
    } else {
      this.addressItemSelected = null;
      this.onItemChange.emit(this.addressItemSelected);
    }
  }

  private searchBestAddressCandidateByTerm() {
    this.addressCandidateSubscription?.unsubscribe();

    if (this.searchTerm) {
      this.opened = false;
      if (this.searchTerm === this.lastSearchTerm) {
        return;
      }

      this.lastSearchTerm = this.searchTerm;
      this.searchBestAddressCandidate({
        text: this.searchTerm,
      });
    } else {
      this.addressItemSelected = null;
      this.onItemChange.emit(this.addressItemSelected);
    }
  }

  private searchBestAddressCandidate(
    options: AddressCandidateSearchBestOptions
  ) {
    this.searchLoading.show();
    this.addressCandidateSubscription = this.addressService
      .findBestAddressCandidate(options)
      .pipe(
        finalize(() => {
          this.searchLoading.hide();
        })
      )
      .subscribe({
        next: (res) => {
          this.addressItemSelected = res;
          this.onItemChange.emit(this.addressItemSelected);
        },
      });
  }

  private handleSearchTerm(text: string) {
    const values = text
      ?.split(',')
      ?.slice(0, 2)
      ?.map((x) => x.trim())
      ?.filter((x) => !!x)
      ?.map((x) => Number(x))
      ?.filter((x) => !Number.isNaN(x) && Number.isFinite(x));
    if (values?.length === 2) {
      text = values.reverse().join(' ');
    }

    return text;
  }
}
